import { useCallback, useEffect, useState } from 'react';
import styles from './AgentOverview.module.css';
import { get } from '../../helpers/api';
import { RechargeRecordModal } from '../RechargeRecordModal/RechargeRecordModal';
import { ActivationLogModal } from '../ActivationLogModal/ActivationLogModal';
import { RecycleRecordModal } from '../RecycleRecordModal/RecycleRecordModal';

type OverviewStats = {
  rechargeAmount: number;
  activationCount: number;
  recycleCount: number;
  recycleAmount: number;
  rechargeChange?: number | null;
  activationChange?: number | null;
  recycleChange?: number | null;
};

const TrendIcon = ({ dir }: { dir: 'up' | 'down' }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {dir === 'up' ? (
      <>
        <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" />
        <polyline points="17 6 23 6 23 12" />
      </>
    ) : (
      <>
        <polyline points="23 18 13.5 8.5 8.5 13.5 1 6" />
        <polyline points="17 18 23 18 23 12" />
      </>
    )}
  </svg>
);

type Period = {
  key: number;
  label: string;
};

const PERIODS: Period[] = [
  { key: 1, label: '今日' },
  { key: 2, label: '昨日' },
  { key: 3, label: '七天' },
  { key: 4, label: '本月' },
  { key: 5, label: '上月' },
];

const formatYuan = (n: number) =>
  `¥${Number(n || 0).toLocaleString('zh-CN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const RechargeIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="6" width="20" height="13" rx="3" />
    <path d="M2 10h20" />
    <circle cx="12" cy="14.5" r="2.4" />
  </svg>
);

const ActivateIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M13 2 4 14h7l-1 8 9-12h-7z" />
  </svg>
);

const RecycleIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 12a9 9 0 0 1 15.5-6.2L21 8" />
    <path d="M21 3v5h-5" />
    <path d="M21 12a9 9 0 0 1-15.5 6.2L3 16" />
    <path d="M3 21v-5h5" />
  </svg>
);

const Change = ({ value }: { value?: number | null }) => {
  const cls =
    value == null
      ? styles.changeNew
      : value > 0
        ? styles.changeUp
        : value < 0
          ? styles.changeDown
          : styles.changeFlat;
  return (
    <div className={`${styles.change} ${cls}`}>
      {value == null && '--'}
      {value === 0 && '-'}
      {value != null && value > 0 && <TrendIcon dir="up" />}
      {value != null && value < 0 && <TrendIcon dir="down" />}
      {value != null && value !== 0 && <span>{Math.abs(value).toFixed(1)}%</span>}
    </div>
  );
};

export const AgentOverview = () => {
  const [period, setPeriod] = useState(4);
  const [stats, setStats] = useState<OverviewStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [recordsOpen, setRecordsOpen] = useState(false);
  const [activationOpen, setActivationOpen] = useState(false);
  const [recycleOpen, setRecycleOpen] = useState(false);

  const fetchStats = useCallback(async (p: number) => {
    setLoading(true);
    setError(null);
    try {
      const data = await get<OverviewStats>(`/admin/tel/proxy/overview?period=${p}`);
      setStats(data);
    } catch (e) {
      setError(e instanceof Error ? e.message : '加载失败');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStats(period);
  }, [period, fetchStats]);

  return (
    <div className={`card ${styles.overview}`}>
      <div className={styles.periods}>
        {PERIODS.map((item) => (
          <button
            key={item.key}
            type="button"
            className={`${styles.periodBtn} ${period === item.key ? styles.periodActive : ''}`}
            onClick={() => setPeriod(item.key)}
          >
            {item.label}
          </button>
        ))}
      </div>
      <div className={`${styles.grid} ${loading ? styles.loading : ''}`}>
        <button
          type="button"
          className={`${styles.stat} ${styles.clickable}`}
          onClick={() => setRecordsOpen(true)}
        >
          <div className={`stat-icon green`}>
            <RechargeIcon />
          </div>
          <div className={styles.statLabel}>充值金额</div>
          <div className={`${styles.statValue} ${styles.greenText}`}>
            {formatYuan(stats?.rechargeAmount ?? 0)}
          </div>
          <Change value={stats?.rechargeChange} />
        </button>
        <button
          type="button"
          className={`${styles.stat} ${styles.clickable}`}
          onClick={() => setActivationOpen(true)}
        >
          <div className={`stat-icon blue`}>
            <ActivateIcon />
          </div>
          <div className={styles.statLabel}>激活数</div>
          <div className={styles.statValue}>
            {Number(stats?.activationCount ?? 0).toLocaleString()}
          </div>
          <Change value={stats?.activationChange} />
        </button>
        <button
          type="button"
          className={`${styles.stat} ${styles.clickable}`}
          onClick={() => setRecycleOpen(true)}
        >
          <div className={`stat-icon orange`}>
            <RecycleIcon />
          </div>
          <div className={styles.statLabel}>回收数</div>
          <div className={`${styles.statValue} ${styles.orangeText}`}>
            {Number(stats?.recycleCount ?? 0).toLocaleString()}
          </div>
          <Change value={stats?.recycleChange} />
        </button>
      </div>
      {error && <div className="error">{error}</div>}
      <RechargeRecordModal
        open={recordsOpen}
        onOpenChange={setRecordsOpen}
        period={period}
      />
      <ActivationLogModal
        open={activationOpen}
        onOpenChange={setActivationOpen}
        period={period}
      />
      <RecycleRecordModal
        open={recycleOpen}
        onOpenChange={setRecycleOpen}
        period={period}
      />
    </div>
  );
};
